import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FiLogIn, FiLogOut, FiUser, FiZap, FiActivity, FiChevronDown } from "react-icons/fi";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import { isSupabaseConfigured } from "../lib/supabaseClient";
import { openBillingPortal, PAID_CHECKOUT_ENABLED } from "../lib/billing";
import "./AuthControl.css";

const MENU_MIN_WIDTH = 220;
const MENU_GAP = 6;
const VIEWPORT_PAD = 8;

function computeMenuStyle(triggerEl) {
  if (!triggerEl) return null;
  const rect = triggerEl.getBoundingClientRect();
  const maxHeight = Math.max(160, window.innerHeight - rect.bottom - MENU_GAP - VIEWPORT_PAD);
  let left = rect.right - MENU_MIN_WIDTH;
  left = Math.max(VIEWPORT_PAD, Math.min(left, window.innerWidth - MENU_MIN_WIDTH - VIEWPORT_PAD));
  return {
    position: "fixed",
    top: rect.bottom + MENU_GAP,
    left,
    minWidth: Math.max(MENU_MIN_WIDTH, rect.width),
    maxHeight,
  };
}

export default function AuthControl() {
  const {
    isLoggedIn,
    isPaid,
    tier,
    user,
    session,
    signOut,
    openLoginModal,
    openPlanModal,
    openUsageModal,
  } = useAuth();
  const { texts } = useLanguage();
  const t = texts.auth || {};
  const tb = texts.billing || {};
  const tu = texts.usage || {};
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  const close = useCallback(() => setOpen(false), []);

  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;
    setMenuStyle(computeMenuStyle(triggerRef.current));
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      setMenuStyle(null);
      return undefined;
    }
    updatePosition();
    const onReposition = () => updatePosition();
    window.addEventListener("resize", onReposition);
    // capture scroll from any ancestor (header overflow-x, etc.)
    window.addEventListener("scroll", onReposition, true);
    return () => {
      window.removeEventListener("resize", onReposition);
      window.removeEventListener("scroll", onReposition, true);
    };
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      const t = e.target;
      if (triggerRef.current?.contains(t)) return;
      if (menuRef.current?.contains(t)) return;
      close();
    };
    const onKey = (e) => {
      if (e.key === "Escape") close();
    };
    // next tick so the opening click does not immediately close
    const id = window.setTimeout(() => {
      document.addEventListener("mousedown", onDoc);
      document.addEventListener("keydown", onKey);
    }, 0);
    return () => {
      window.clearTimeout(id);
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, close]);

  if (!isSupabaseConfigured) return null;

  const tierLabel = () => {
    if (tier === "light") return t.planLight || "Light";
    if (tier === "pro") return t.planPro || "Pro";
    if (tier === "business") return t.planBusiness || "Business";
    return t.planFree || "Free";
  };

  const handleManage = async () => {
    if (!session?.access_token) return;
    setBusy(true);
    try {
      await openBillingPortal({
        accessToken: session.access_token,
        returnUrl: window.location.origin,
      });
    } catch {
      openPlanModal();
    } finally {
      setBusy(false);
      close();
    }
  };

  if (!isLoggedIn) {
    return (
      <div className="auth-control">
        <button type="button" className="auth-control__btn" onClick={openLoginModal}>
          <FiLogIn /> {t.login || "ログイン"}
        </button>
      </div>
    );
  }

  const isDark =
    typeof document !== "undefined" &&
    Boolean(document.querySelector(".app.dark, .landing-page.dark, .dark"));

  const menu =
    open &&
    menuStyle &&
    typeof document !== "undefined" &&
    createPortal(
      <div
        ref={menuRef}
        className={`auth-control__dropdown${isDark ? " dark" : ""}`}
        role="menu"
        style={menuStyle}
      >
        <div className="auth-control__email-row" title={user?.email || ""}>
          {user?.email}
        </div>
        <button
          type="button"
          className="auth-control__item"
          role="menuitem"
          onClick={() => {
            // Close menu first, then open centered UsageModal (portaled to body)
            close();
            window.requestAnimationFrame(() => openUsageModal());
          }}
        >
          <FiActivity /> {tu.short || "利用状況"}
        </button>
        {!isPaid ? (
          <button
            type="button"
            className="auth-control__item auth-control__item--accent"
            role="menuitem"
            onClick={() => {
              close();
              if (PAID_CHECKOUT_ENABLED) {
                window.requestAnimationFrame(() => openPlanModal());
              }
            }}
            disabled={!PAID_CHECKOUT_ENABLED}
          >
            <FiZap />{" "}
            {PAID_CHECKOUT_ENABLED
              ? tb.upgrade || "アップグレード"
              : tb.comingSoon || texts.ai?.comingSoon || "準備中"}
          </button>
        ) : (
          <button
            type="button"
            className="auth-control__item"
            role="menuitem"
            onClick={handleManage}
            disabled={busy}
          >
            <FiZap /> {tb.manage || "請求"}
          </button>
        )}
        <button
          type="button"
          className="auth-control__item auth-control__item--danger"
          role="menuitem"
          onClick={() => {
            close();
            signOut();
          }}
        >
          <FiLogOut /> {t.logout || "ログアウト"}
        </button>
      </div>,
      document.body
    );

  return (
    <div className={`auth-control auth-control--menu${open ? " is-open" : ""}`}>
      <button
        ref={triggerRef}
        type="button"
        className="auth-control__trigger"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="menu"
        title={user?.email || ""}
      >
        <FiUser aria-hidden />
        <span className={`auth-control__plan ${isPaid ? "auth-control__plan--paid" : ""}`}>
          {tierLabel()}
        </span>
        <FiChevronDown className="auth-control__chevron" aria-hidden />
      </button>
      {menu}
    </div>
  );
}
